import React from 'react';
import {
  DocPage, PageHeader, Section, SubSection,
  Para, Callout, Prerequisites, NextStep,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Usage.css';

const TOC = [
  { id: 'overview',      label: 'Overview' },
  { id: 'contents',      label: 'What a usage contains' },
  { id: 'measurement',   label: 'The measurement',       indent: true },
  { id: 'context',       label: 'Pricing context',       indent: true },
  { id: 'attribution',   label: 'Customer and product',  indent: true },
  { id: 'timestamp',     label: 'Timestamp',             indent: true },
  { id: 'resolution',    label: 'Contract resolution' },
  { id: 'partial',       label: 'Partial ingestion' },
  { id: 'ref-id',        label: 'Usage reference ID' },
  { id: 'dedup',         label: 'Deduplication' },
  { id: 'to-charge',     label: 'From usage to charge' },
  { id: 'relationship',  label: 'Relationship to other concepts' },
];

// ── Usage event card ──────────────────────────────────────────────────────────

function UsageEventCard({ u }) {
  return (
    <div className="usage-card">
      <div className="usage-card-header">
        <span className="usage-card-label">Usage Event</span>
        <span className="usage-card-product">{u.product}</span>
      </div>
      <div className="usage-card-rows">

        <div className="usage-card-row usage-card-row--measurement">
          <div className="usage-card-row-label">Measurement</div>
          <div className="usage-card-row-value">
            {u.usageAttr.label} — {u.usageAttr.value} {u.usageAttr.unit}
          </div>
        </div>

        <div className="usage-card-row usage-card-row--context">
          <div className="usage-card-row-label">Pricing context</div>
          <div className="usage-card-row-value">
            {u.rateAttrs.map((attr) => (
              <span key={attr.label} className="usage-card-tag">
                <span className="usage-card-tag-key">{attr.label}</span>
                <span className="usage-card-tag-sep"> = </span>
                <span className="usage-card-tag-val">{attr.value}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="usage-card-row">
          <div className="usage-card-row-label">Customer</div>
          <div className="usage-card-row-value">{u.customer}</div>
        </div>

        <div className="usage-card-row">
          <div className="usage-card-row-label">Product</div>
          <div className="usage-card-row-value">{u.product}</div>
        </div>

        <div className="usage-card-row usage-card-row--timestamp">
          <div className="usage-card-row-label">Timestamp</div>
          <div className="usage-card-row-value">{u.timestamp}</div>
        </div>

      </div>
    </div>
  );
}

// ── Partial ingestion resolution table ────────────────────────────────────────

function ResolutionTable({ pi }) {
  return (
    <div className="usage-resolution">
      <div className="usage-resolution-header">
        Usage sent with{' '}
        <strong>{pi.rateAttr.label} = {pi.rateAttr.value}</strong>
        {' '}— how each measurement resolves:
      </div>
      {pi.measurements.map((m) => (
        <div key={m.attr} className="usage-resolution-row">
          <div className="usage-resolution-cell">
            <div className="usage-resolution-attr">{m.attr}</div>
            <div className="usage-resolution-value">{m.value}</div>
          </div>
          <div className="usage-resolution-cell usage-resolution-reason">
            {m.reason}
          </div>
          <div className="usage-resolution-cell">
            <span className={`usage-resolution-badge usage-resolution-badge--${m.matched ? 'matched' : 'skipped'}`}>
              {m.matched ? '✓ Accumulated' : '— Skipped'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Grouping comparison ───────────────────────────────────────────────────────

function GroupingComparison({ g }) {
  return (
    <>
      <p className="usage-grouping-tiers-note">{g.tiersNote}</p>
      <div className="usage-grouping">

        {/* Left: separate pricing */}
        <div className="usage-grouping-panel">
          <div className="usage-grouping-panel-header">
            <div className="usage-grouping-panel-title">Different reference IDs → priced separately</div>
          </div>
          <div className="usage-grouping-body">
            {g.records.map((r, i) => (
              <div key={i} className="usage-grouping-record">
                <div className={`usage-grouping-record-ref usage-grouping-record-ref--${i === 0 ? 'a' : 'b'}`}>ref: {g.separate.refIds[i]}</div>
                <div className="usage-grouping-record-qty">{r.qty}</div>
                <div className="usage-grouping-record-calc">{g.separate.results[i].calc}</div>
                <div className="usage-grouping-record-charge">{g.separate.results[i].charge}</div>
              </div>
            ))}
          </div>
          <div className="usage-grouping-total">
            <span className="usage-grouping-total-label">Total</span>
            <span className="usage-grouping-total-amount">{g.separate.total}</span>
          </div>
        </div>

        {/* Right: grouped pricing */}
        <div className="usage-grouping-panel usage-grouping-panel--grouped">
          <div className="usage-grouping-panel-header">
            <div className="usage-grouping-panel-title">Same reference ID → aggregated first</div>
          </div>
          <div className="usage-grouping-body">
            {g.records.map((r, i) => (
              <div key={i} className="usage-grouping-record">
                <div className="usage-grouping-record-ref usage-grouping-record-ref--same">ref: {g.grouped.refId}</div>
                <div className="usage-grouping-record-qty">{r.qty}</div>
              </div>
            ))}
            <div className="usage-grouping-aggregate">
              <div className="usage-grouping-aggregate-label">↓ aggregated</div>
              <div className="usage-grouping-aggregate-qty">{g.grouped.aggregatedQty}</div>
              {g.grouped.tiers.map((t, i) => (
                <div key={i} className="usage-grouping-tier">
                  <span>{t.calc}</span>
                  <span className="usage-grouping-tier-charge">{t.charge}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="usage-grouping-total">
            <span className="usage-grouping-total-label">Total</span>
            <span className="usage-grouping-total-amount">{g.grouped.total}</span>
          </div>
        </div>

      </div>
    </>
  );
}

// ── Deduplication card ────────────────────────────────────────────────────────

function DedupCard({ d }) {
  return (
    <div className="usage-dedup">
      <div className="usage-dedup-header">
        <span className="usage-dedup-batch-label">Batch ID</span>
        <span className="usage-dedup-batch-id">{d.batchId}</span>
      </div>
      <div className="usage-dedup-row usage-dedup-row--replaced">
        <span className="usage-dedup-icon">✕</span>
        <div className="usage-dedup-body">
          <div className="usage-dedup-value">{d.attr} — {d.first.value}</div>
          <div className="usage-dedup-note">{d.first.note}</div>
        </div>
        <span className="usage-dedup-badge usage-dedup-badge--replaced">Replaced</span>
      </div>
      <div className="usage-dedup-row usage-dedup-row--active">
        <span className="usage-dedup-icon">✓</span>
        <div className="usage-dedup-body">
          <div className="usage-dedup-value">{d.attr} — {d.second.value}</div>
          <div className="usage-dedup-note">{d.second.note}</div>
        </div>
        <span className="usage-dedup-badge usage-dedup-badge--active">Billed</span>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Usage({ onNavigate }) {
  const { industry } = useIndustry();
  const u = industry.usage;
  const pi = u.partialIngestion;
  const g = industry.usageGrouping;
  const d = u.dedup;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Operations"
        title="Usage"
        description="A usage record is an event submitted to the billing engine carrying a meter value, pricing context, and a timestamp. The engine aggregates these across the billing period to produce invoice charges."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'meter', label: 'Meters' },
          { id: 'contract',        label: 'Contracts' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          For flat-rate billing, a contract alone is enough to generate an invoice — the
          charges are fixed, and the system only needs to know the billing cycle dates.
          Metered billing is different. The charge depends on what the customer actually
          did, so the system needs a record of that activity before it can calculate
          anything.
        </Para>
        <Para>
          A usage is that record. Each usage represents one consumable event — a shipment
          dispatched, an API call made, a transaction processed. Usages are ingested as
          activity happens, throughout the billing period.
        </Para>
        <Callout type="info" title="Usages don't immediately produce charges">
          Receiving a usage doesn't trigger an immediate invoice. Instead, usages accumulate
          over the billing period. When the period closes, the system applies the rate card
          to the total accumulated quantity and generates the invoice line.
        </Callout>
      </Section>

      <Section id="contents" title="What a usage contains">
        <Para>
          Here is an example usage for {u.customer}:
        </Para>
        <UsageEventCard u={u} />
        <Para>
          Each field serves a specific purpose. The sections below explain what each one
          means and why it is required.
        </Para>

        <SubSection id="measurement" title="The measurement">
          <Para>
            The core of a usage is the measurement — a meter name and its value.
            In this example, the attribute is <em>{u.usageAttr.label}</em> and the value
            is <em>{u.usageAttr.value} {u.usageAttr.unit}</em>. This is the quantity the
            billing engine will accumulate and eventually price.
          </Para>
          <Para>
            The meter must already be defined on the product. When the billing
            engine receives a usage, it looks up the attribute on the product to understand
            the unit of measurement and which fee components it drives.
          </Para>
        </SubSection>

        <SubSection id="context" title="Pricing context">
          <Para>
            When a product's fee components use pricing dimensions, the usage must also carry
            those attribute values. In this example, the fee component is{' '}
            <em>{u.feeComponent}</em>, which is priced by{' '}
            {u.rateAttrs.map((a, i) => (
              <span key={a.label}>
                {i > 0 && ' and '}<em>{a.label}</em>
              </span>
            ))}.
            The usage provides{' '}
            {u.rateAttrs.map((a, i) => (
              <span key={a.label}>
                {i > 0 && ' and '}<em>{a.label} = {a.value}</em>
              </span>
            ))}{' '}
            so the billing engine can select the correct rate card entry.
          </Para>
        </SubSection>

        <SubSection id="attribution" title="Customer and product">
          <Para>
            Every usage must be attributed to a customer and a product.
          </Para>
          <Para>
            The <strong>customer</strong> identifies whose activity this is. A customer
            can have multiple active contracts. The billing engine checks all of them and
            accumulates the usage against every contract where a matching rate card is
            found. There is no need to specify which contract — the system resolves this
            automatically.
          </Para>
          <Para>
            The <strong>product</strong> scopes the meter. The same attribute
            name can exist on multiple products — specifying the product tells the system
            which definition to use, and which fee components to check for rate card matches.
          </Para>
        </SubSection>

        <SubSection id="timestamp" title="Timestamp">
          <Para>
            Every usage carries a timestamp recording when the event occurred. In the
            example above, the event happened on <em>{u.timestamp}</em>.
          </Para>
          <Callout type="warning" title="The timestamp determines the billing cycle">
            The billing engine uses the timestamp to decide which billing period this
            usage belongs to. {u.cycleNote} A usage timestamped outside the active
            billing period will be assigned to whichever period contains that date — or
            rejected if no period matches.
          </Callout>
          <Para>
            This matters especially for events that arrive late. If a delivery that
            happened in January is reported in February, the timestamp still marks it
            as a January event. It accumulates toward the January invoice, not February's.
          </Para>
        </SubSection>
      </Section>

      <Section id="resolution" title="Contract resolution">
        <Para>
          A usage only identifies the customer — not a specific contract. When a usage
          arrives, the billing engine scans all active contracts for that customer and
          accumulates the usage against every contract where it finds a matching rate card.
        </Para>
        <Para>
          This means a single usage event can land on multiple contracts simultaneously.
          For example, if {u.customer} has two active contracts — both covering the same
          product — and both have rate cards that match the usage, the measurement
          accumulates toward both.
        </Para>
        <Callout type="tip" title="You don't need to route usages to contracts">
          The system handles resolution automatically. Your systems just need to send the
          customer, the product, the measurements, and the pricing context. The billing
          engine figures out where each usage belongs.
        </Callout>
      </Section>

      <Section id="partial" title="Partial ingestion">
        <Para>
          A single usage can carry measurements for more than one meter at once.
          The billing engine tries to resolve each measurement independently — and it is
          perfectly normal for some to match and others not to.
        </Para>
        <Para>
          Consider this scenario: {u.customer} has two contracts. {pi.contracts[0].label}{' '}
          has a fee component <em>{pi.contracts[0].feeComponent}</em> driven by{' '}
          <em>{pi.contracts[0].usageAttr}</em>, with a rate card configured for{' '}
          <em>{pi.rateAttr.label} = {pi.rateAttr.value}</em>. {pi.contracts[1].label} has
          a fee component <em>{pi.contracts[1].feeComponent}</em> driven by{' '}
          <em>{pi.contracts[1].usageAttr}</em>, but no rate card has been configured for{' '}
          <em>{pi.rateAttr.label} = {pi.rateAttr.value}</em>.
        </Para>
        <Para>
          A usage is sent with both measurements and{' '}
          <em>{pi.rateAttr.label} = {pi.rateAttr.value}</em>:
        </Para>
        <ResolutionTable pi={pi} />
        <Para>
          The <em>{pi.measurements[0].attr}</em> measurement finds a rate card and
          accumulates. The <em>{pi.measurements[1].attr}</em> measurement finds no rate
          card for the given context, so it is skipped. The usage request as a whole is
          not rejected — only the unresolvable portion is dropped. This is called{' '}
          <strong>partial ingestion</strong>.
        </Para>
        <Callout type="info" title="Skipped measurements are not errors">
          Partial ingestion is expected behaviour, not a failure. It allows you to send
          a complete snapshot of activity in one request, even when not all measurements
          are billable under every contract. The system silently ignores what it cannot
          resolve and processes what it can.
        </Callout>
      </Section>

      <Section id="ref-id" title="Usage reference ID">
        <Para>
          When multiple usage records come in for the same fee component, there is an
          important question about how pricing should be applied: should each record be
          priced on its own and the amounts summed, or should all the records be aggregated
          first and then priced as a single quantity?
        </Para>
        <Para>
          The answer depends on the type of rate card. For a simple flat rate, it makes
          no difference — pricing 30 units and 40 units separately gives the same result
          as pricing 70 units together. But for tiered pricing, it matters a great deal.
          Grouping records that together cross a tier boundary produces a lower total than
          pricing each record individually (where neither alone crosses the boundary).
        </Para>
        <Para>
          The <strong>usage reference ID</strong> is how you control this. Records that
          share the same reference ID are grouped together and treated as a single unit for
          pricing. The rate card is applied once on the combined quantity, rather than once
          per record.
        </Para>
        <GroupingComparison g={g} />
        <Para>
          In this example, both records are for <em>{g.feeComponent}</em>. When they carry
          different reference IDs, each is priced independently — both land in Tier 1 and
          the total is <em>{g.separate.total}</em>. When they share the same reference ID,
          they are aggregated first: the combined quantity crosses into Tier 2, and the
          total drops to <em>{g.grouped.total}</em>.
        </Para>
        <Callout type="info" title="Grouping only works within the same accumulation window">
          Records are grouped by reference ID only when they fall within the same
          accumulation window — the billing period defined by the contract. Reference IDs
          do not span across periods.
        </Callout>
      </Section>

      <Section id="dedup" title="Deduplication">
        <Para>
          The batch ID is an optional parameter on each usage ingestion request. If you
          don't include one, the system assigns a random value automatically — which means
          every request without a batch ID is treated as a distinct, new submission.
        </Para>
        <Para>
          When you provide a batch ID yourself, you take control of that behaviour. If a
          second request arrives with the same batch ID as a previous one, the system
          discards the earlier submission and bills based on the latest one instead.
        </Para>
        <Para>
          This covers two common situations. The first is a <strong>safe retry</strong>: your
          system sends a request but doesn't receive a confirmation, so it sends again. By
          reusing the same batch ID, you guarantee the usage is counted exactly once no
          matter how many times the request is sent. The second is a{' '}
          <strong>correction</strong>: you realise a submitted value was wrong and need to
          replace it. Send the corrected request with the same batch ID and the original is
          overwritten.
        </Para>
        <DedupCard d={d} />
        <Para>
          In this example, the first submission recorded <em>{d.first.value}</em> for{' '}
          <em>{d.attr}</em>. The second submission — same batch ID, corrected value —
          replaced it. Only <em>{d.second.value}</em> is used for billing.
        </Para>
        <Callout type="tip" title="Include a batch ID whenever your pipeline can retry">
          If your system retries failed requests, always include a stable batch ID derived
          from the source event — a shipment ID, a transaction ID, or anything that
          uniquely identifies the underlying activity. Without it, retries produce
          duplicate usage entries.
        </Callout>
      </Section>

      <Section id="to-charge" title="From usage to charge">
        <Para>
          A single usage event is just a data point. The billing engine turns it into a
          charge through a sequence of steps:
        </Para>
        <div className="usage-flow">
          <div className="usage-flow-step">
            Usage received
            <span className="usage-flow-sub">product, customer, measurements, pricing context, timestamp</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step">
            Contracts identified
            <span className="usage-flow-sub">all active contracts for the customer are checked</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step">
            Rate cards matched
            <span className="usage-flow-sub">each measurement is resolved against each contract's fee components</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step usage-flow-step--highlight">
            Quantity accumulated
            <span className="usage-flow-sub">matched measurements are added to the running total for the billing period</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step usage-flow-step--dim">
            Period closes → rate card applied → invoice line generated
          </div>
        </div>
        <Para>
          Everything in the middle steps is invisible to the customer. They see only the
          final invoice line: the fee component name, the total quantity billed, and the
          amount charged.
        </Para>
      </Section>

      <Section id="relationship" title="Relationship to other concepts">
        <Para>
          A usage sits at the boundary between your systems and the billing engine. It
          references entities set up earlier in the catalog and contract, and feeds into
          the invoice at period end:
        </Para>
        <div className="usage-flow">
          <div className="usage-flow-step usage-flow-step--dim">
            Product · Meter · Pricing Dimension · Fee Component · Rate Card
            <span className="usage-flow-sub">catalog — defined once</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step usage-flow-step--dim">
            Customer · Contract (one or more)
            <span className="usage-flow-sub">per customer — defines billing cycles and rates</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step usage-flow-step--highlight">
            Usage
            <span className="usage-flow-sub">runtime — ingested as activity happens, resolved across all matching contracts</span>
          </div>
          <div className="usage-flow-arrow">↓</div>
          <div className="usage-flow-step usage-flow-step--dim">
            Invoice
            <span className="usage-flow-sub">generated at end of each billing period</span>
          </div>
        </div>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'rate-card', title: 'Rate Cards', desc: 'The pricing rules that convert accumulated usage quantity into a charge.' },
          { id: 'contract',  title: 'Contracts',  desc: 'Contracts define the billing cycles that govern when usages are invoiced.' },
        ]}
      />
    </DocPage>
  );
}
